import {createFragmentContainer, graphql} from 'react-relay';
import React from 'react';

import {useIntl} from 'react-intl';
import {
	getBillableExpenseTotals,
	getFixedPriceExpenseTotals,
	NUMBER_TYPE,
} from '../../../../components/new-ui/project/project-budget-v3/util/BudgetUtils';
import FinancialNumber from '../../../../forecast-app/shared/components/financials/FinancialNumber';
import {isYearMonthDayBeforeToday} from '../../../../forecast-app/shared/util/DateUtil';
import {SummaryCard, Tooltip} from '@forecast-it/design-system';
import ProjectUtil from '../../../../forecast-app/shared/util/ProjectUtil';
import {BUDGET_TYPE} from '../../../../constants';

const getInvoiceCountText = (length, altTextTag, formatMessage) => {
	return length
		? length + ' ' + formatMessage({id: length !== 1 ? 'common.invoices' : 'common.invoice'})
		: formatMessage({id: altTextTag});
};

const tooltipRevenueFromExpenses = formatMessage => {
	return {
		title: formatMessage({id: 'project_budget.revenue_from_expenses'}),
		details: formatMessage({id: 'project_budget.revenue_from_expenses_tooltip'}),
	};
};

const ProjectInvoicingTotals = ({project, company}) => {
	const intl = useIntl();
	const formatMessage = intl.formatMessage;
	const currency = project.rateCard?.currency || company.currency;
	const isFixedPrice = ProjectUtil.isFixedPriceProject(project);

	const invoices = project.invoices?.edges ? project.invoices.edges.map(e => e.node) : [];
	const invoiceTotal = invoices.reduce((acc, invoice) => acc + invoice.invoicedWithoutTaxProjectCurrency, 0);

	const sendOrExportedInvoices = invoices.filter(invoice => invoice.status === 'SENT' || invoice.status === 'EXPORTED');
	const sendOrExportedInvoiceTotal = sendOrExportedInvoices.reduce(
		(acc, invoice) => acc + invoice.invoicedWithoutTaxProjectCurrency,
		0
	);

	const invoicesWithPayments = invoices.filter(invoice => invoice.paidWithoutTaxProjectCurrency > 0);
	const invoicePaymentTotal = invoicesWithPayments.reduce((acc, invoice) => acc + invoice.paidWithoutTaxProjectCurrency, 0);

	const overdueInvoices = invoices.filter(invoice =>
		isYearMonthDayBeforeToday(invoice.dueYear, invoice.dueMonth, invoice.dueDay)
	);
	const overdueInvoiceTotal = overdueInvoices.reduce((acc, invoice) => acc + invoice.unpaidWithoutTaxProjectCurrency, 0);

	// A Fixed Price Deliverable is a Deliverable on a Time & Materials project that has been given a fixed
	// fee. That fee is what the client owes for it, and it already covers every time registration and
	// expense attached to it, so both the expense and the time terms below have to leave those items out
	// to avoid counting the same revenue twice.
	const isTimeAndMaterials = project.budgetType === BUDGET_TYPE.TIME_AND_MATERIALS;

	// The id set and the fee total below are both derived from this one list, so the "has a fixed fee"
	// rule is stated once here rather than repeated at each use.
	const pricedDeliverableEdges = isTimeAndMaterials
		? (project.deliverables?.edges || []).filter(edge => edge.node?.price != null)
		: [];

	const pricedDeliverableIds = new Set(
		// deliverableId on the grouped financial numbers is the raw database id, while the
		// Deliverable's own id is a global id, so decode before comparing the two.
		pricedDeliverableEdges.map(edge => Number(atob(edge.node.id).split(':')[1]))
	);

	// Projects that never give a Deliverable a fixed fee are not using the feature at all, so the card drops
	// every Deliverable-specific line and reverts to the plain Time & Materials wording. Note this is about
	// the feature being in use, not about the amount: a priced Deliverable with nothing recognized yet still
	// gets its line, showing zero.
	const hasPricedDeliverables = pricedDeliverableIds.size > 0;

	// Expenses attached to a Fixed Price Deliverable are already covered by that Deliverable's recognized
	// revenue, so they must not also be added as raw billable expenses.
	const billableExpenseEdges = (project.expenseItems?.edges || []).filter(
		edge => !(isTimeAndMaterials && edge.node?.deliverable != null && edge.node.deliverable.price != null)
	);

	const revenueFromExpenses = isFixedPrice
		? getFixedPriceExpenseTotals(project.expenseItems?.edges)
		: getBillableExpenseTotals(billableExpenseEdges);

	// billableActualTimeAndExpenses is the billable value of the service delivered, so it counts time
	// registered against a Fixed Price Deliverable as well. That work's value is instead represented by the
	// Deliverable's own fixed fee in the Deliverable line below, so it has to come out here — otherwise the
	// same work is paid for twice in the total.
	const pricedDeliverableBillableTime = (project.deliverableTimeFinancialNumbers || [])
		.filter(row => row.deliverableId != null && pricedDeliverableIds.has(Number(row.deliverableId)))
		.reduce((acc, row) => acc + row.billableActualTimeAndExpenses, 0);

	const billableTime = isFixedPrice
		? project.financialNumbers.totalActualRevenueRecognition
		: project.timeFinancialNumbers.billableActualTimeAndExpenses - pricedDeliverableBillableTime;

	// The fee itself, not the revenue recognized against it so far: a Deliverable is worth its full price
	// from the moment it is given one, whether or not its time has been booked and its expenses incurred.
	// Time on an unpriced Deliverable bills as ordinary Time & Materials work and is therefore already part
	// of billableTime, so only Deliverables with a fixed fee contribute here.
	const deliverableRevenue = pricedDeliverableEdges.reduce((acc, edge) => acc + edge.node.price, 0);

	return (
		<SummaryCard.Container wrap={'true'}>
			{isFixedPrice ? (
				<SummaryCard
					title={formatMessage({id: 'common.total_revenue'})}
					subtitle={''}
					flexBasis={'300px'}
					flexGrow={'2'}
				>
					<FinancialNumber.Grid>
						<FinancialNumber
							cy={'projectTotalRevenue'}
							value={project.financialNumbers.totalRevenueRecognition}
							numberType={NUMBER_TYPE.MONEY}
							variant={'l'}
							currency={currency}
							zeroValuesHidden={true}
						/>

						<FinancialNumber
							labelTranslationId={
								project.budgetType === BUDGET_TYPE.RETAINER
									? 'retainer.total_project_target'
									: 'project_budget.project_fixed_price'
							}
							cy={'projectFixedPrice'}
							value={project.budget}
							numberType={NUMBER_TYPE.MONEY}
							type={'label'}
							variant={'m'}
							currency={currency}
						/>

						<FinancialNumber
							labelTranslationId={'project_budget.revenue_from_expenses'}
							cy={'projectExpenseRevenue'}
							value={revenueFromExpenses}
							numberType={NUMBER_TYPE.MONEY}
							type={'label'}
							variant={'m'}
							tooltipItem={tooltipRevenueFromExpenses(formatMessage)}
							tooltipMaxWidth={'300px'}
							currency={currency}
						/>
					</FinancialNumber.Grid>
				</SummaryCard>
			) : (
				project.budgetType !== BUDGET_TYPE.NON_BILLABLE && (
					<SummaryCard
						title={formatMessage({id: 'common.confirmed_revenue'})}
						titleTooltip={
							<Tooltip.Item
								title={formatMessage({id: 'common.confirmed_revenue'})}
								details={formatMessage({
									id: hasPricedDeliverables
										? 'project_budget.confirmed_revenue.tooltip_with_deliverables'
										: 'project_budget.confirmed_revenue.tooltip',
								})}
							/>
						}
						subtitle={''}
						flexBasis={'300px'}
						flexGrow={'2'}
					>
						<FinancialNumber.Grid>
							<FinancialNumber
								cy={'projectTotalRevenue'}
								value={billableTime + revenueFromExpenses + deliverableRevenue}
								numberType={NUMBER_TYPE.MONEY}
								variant={'l'}
								currency={currency}
								zeroValuesHidden={true}
							/>

							<FinancialNumber
								labelTranslationId={'project_budget.actual_billable_time_short'}
								cy={'actualBillableTime'}
								value={billableTime}
								numberType={NUMBER_TYPE.MONEY}
								type={'label'}
								variant={'m'}
								currency={currency}
								tooltipItem={
									hasPricedDeliverables
										? {
												title: formatMessage({id: 'project_budget.actual_billable_time_short'}),
												details: formatMessage({
													id: 'project_budget.actual_billable_time.tooltip',
												}),
										  }
										: undefined
								}
								tooltipMaxWidth={'300px'}
							/>

							<FinancialNumber
								labelTranslationId={'expenses.billable_expenses'}
								cy={'projectExpenseRevenue'}
								value={revenueFromExpenses}
								numberType={NUMBER_TYPE.MONEY}
								type={'label'}
								variant={'m'}
								tooltipItem={{
									title: formatMessage({id: 'expenses.billable_expenses'}),
									details: formatMessage({
										id: hasPricedDeliverables
											? 'project_budget.billable_expenses.tooltip_with_deliverables'
											: 'project_budget.billable_expenses.tooltip',
									}),
								}}
								tooltipMaxWidth={'300px'}
								currency={currency}
							/>

							{hasPricedDeliverables && (
								<FinancialNumber
									labelTranslationId={'project_budget.fixed_price_deliverables_revenue'}
									cy={'projectDeliverableRevenue'}
									value={deliverableRevenue}
									numberType={NUMBER_TYPE.MONEY}
									type={'label'}
									variant={'m'}
									currency={currency}
									tooltipItem={{
										title: formatMessage({id: 'project_budget.fixed_price_deliverables_revenue'}),
										details: formatMessage({
											id: 'project_budget.fixed_price_deliverables_revenue.tooltip',
										}),
									}}
									tooltipMaxWidth={'300px'}
								/>
							)}
						</FinancialNumber.Grid>
					</SummaryCard>
				)
			)}

			<SummaryCard
				title={formatMessage({id: 'invoicing.invoices_created'})}
				subtitle={getInvoiceCountText(invoices.length, 'invoicing.no_invoices_created', formatMessage)}
			>
				<FinancialNumber.Grid>
					<FinancialNumber
						cy={'invoiceTotal'}
						value={invoiceTotal}
						numberType={NUMBER_TYPE.MONEY}
						variant={'l'}
						currency={currency}
						zeroValuesHidden={true}
					/>
				</FinancialNumber.Grid>
			</SummaryCard>

			<SummaryCard
				title={formatMessage({id: 'invoicing.invoices_send_exported'})}
				subtitle={getInvoiceCountText(
					sendOrExportedInvoices.length,
					'invoicing.no_invoices_sent_or_exported',
					formatMessage
				)}
			>
				<FinancialNumber.Grid>
					<FinancialNumber
						cy={'sendOrExportedInvoiceTotal'}
						value={sendOrExportedInvoiceTotal}
						numberType={NUMBER_TYPE.MONEY}
						variant={'l'}
						currency={currency}
						zeroValuesHidden={true}
					/>
				</FinancialNumber.Grid>
			</SummaryCard>
			<SummaryCard
				title={formatMessage({id: 'invoicing.invoice_payments'})}
				subtitle={getInvoiceCountText(invoicesWithPayments.length, 'invoicing.no_invoice_payments', formatMessage)}
			>
				<FinancialNumber.Grid>
					<FinancialNumber
						cy={'invoicePaymentTotal'}
						value={invoicePaymentTotal}
						numberType={NUMBER_TYPE.MONEY}
						variant={'l'}
						currency={currency}
						zeroValuesHidden={true}
					/>
				</FinancialNumber.Grid>
			</SummaryCard>
			<SummaryCard
				title={formatMessage({id: 'invoicing.overdue_invoices'})}
				subtitle={getInvoiceCountText(overdueInvoices.length, 'invoicing.no_overdue_invoices', formatMessage)}
			>
				<FinancialNumber.Grid>
					<FinancialNumber
						cy={'overdueInvoiceTotal'}
						value={overdueInvoiceTotal}
						numberType={NUMBER_TYPE.MONEY}
						variant={'l'}
						currency={currency}
						zeroValuesHidden={true}
					/>
				</FinancialNumber.Grid>
			</SummaryCard>
		</SummaryCard.Container>
	);
};

export default createFragmentContainer(ProjectInvoicingTotals, {
	project: graphql`
		fragment ProjectInvoicingTotals_project on ProjectType {
			id
			name
			rateCard {
				currency
			}
			budget
			budgetType
			defaultPeriodBudgetType
			invoices {
				edges {
					node {
						status
						invoicedWithoutTaxProjectCurrency
						paidWithoutTaxProjectCurrency
						unpaidWithoutTaxProjectCurrency
						dueDay
						dueMonth
						dueYear
					}
				}
			}
			timeFinancialNumbers: financialNumbers(
				searchQuery: {filters: [{field: ROLE, operator: NOT_IN, value: "Um9sZTotMQ=="}]}
				convertToProjectCurrency: true
			) {
				billableActualTimeAndExpenses
			}
			financialNumbers(convertToProjectCurrency: true) {
				totalRevenueRecognition
				totalActualRevenueRecognition
				expenseRevenuePercentage
			}
			deliverableTimeFinancialNumbers: groupedFinancialNumbers(
				searchQuery: {filters: [{field: ROLE, operator: NOT_IN, value: "Um9sZTotMQ=="}]}
				convertToProjectCurrency: true
				groupBy: ["DELIVERABLE"]
			) {
				deliverableId
				billableActualTimeAndExpenses
			}
			deliverables(first: 10000) {
				edges {
					node {
						id
						price
					}
				}
			}
			expenseItems(first: 10000) @connection(key: "Project_expenseItems") {
				edges {
					node {
						approved
						billable
						partOfFixedPrice
						expenseYear
						expenseMonth
						expenseDay
						price
						cost
						quantity
						deliverable {
							id
							price
						}
					}
				}
			}
		}
	`,
});
